// @flow
import VehicleModelActions from 'common/redux/actions/vehicleModel';

import { getClientDimensionsMapping } from 'common/redux/selectors/clientSelector';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import {
    MasterVehicleModel,
    FuelTypeEnum,
    FleetTypeEnum,
    LoadingUnloadingSideEnum,
    VehicleTypeEnum
} from 'common/models/internal/MasterVehicleModel';

import { Skill } from 'common/models/internal/Skill';

import {
    vehicleModelMaster as vehicleModelApi,
    skill as skillsApi
} from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import VehicleCapacity from './VehicleCapacity';
import VehicleDimension from './VehicleDimension';
import RiderPersonaLimits from 'common/entity/riderPersonaMaster/RiderPersonaLimits';
import { EnhancedInput } from 'common/components/core/input';
import TransporterIdInput from 'common/entity/riderPersonaMaster/TransporterIdInput';

import DataUtils from 'common/utils/DataUtils';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { volumeUnitOptions } from 'common/enums/VolumeUnitEnum';
import { ENTITY_NAME_ID } from 'common/constants/entity/vehicleModelMaster';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';
import { VehicleCompartmentsInput } from './VehicleCompartments';

export const UnitEnum = {
    Speed: 'Kmph',
    Volume: {
        Meters: 'm',
        CubicMeters: 'm³',
        KiloGram: 'Kg'
    }
};

const transporterIdInputFilters = ({ input }) => {
    let filters = new FilterListBuilder().addAll([
        new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValue('ACTIVE')
    ]);

    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

const FuelTypeOptions = FuelTypeEnum.getOptions({
    translationKey: 'masterVehicleModelProperties.fuelTypes'
});

const FleetTypeOptions = FleetTypeEnum.getOptions({
    translationKey: 'masterVehicleModelProperties.fleetTypesOptions'
});

const LoadingUnloadingSideOptions = LoadingUnloadingSideEnum.getOptions({
    translationKey: 'masterVehicleModelProperties.loadingUnloadingSides'
});

const vehicleTypeOptions = VehicleTypeEnum.getOptions({
    translationKey: 'masterVehicleModelProperties.vehicleTypes'
});

const VEHICLEMODEL_FIELDS = [
    {
        accessor: 'code',
        label: 'vehicleModelForm.code',
        helpText: 'vehicleModelForm.codeTooltip',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.id,
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'vehicleModelForm.name',
        placeholder: 'vehicleModelForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'description',
        label: 'vehicleModelForm.description',
        placeholder: 'vehicleModelForm.descriptionPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'vehicleType',
        type: FieldTypeEnum.Select,
        label: 'vehicleModelForm.vehicleType',
        placeholder: 'vehicleModelForm.vehicleTypePlaceholder',
        required: true,
        options: vehicleTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'transporterId',
        label: 'vehicleModelForm.transporterId',
        placeholder: 'vehicleModelForm.transporterIdPlaceholder',
        helptext: 'vehicleModelForm.transporterIdTooltip',
        required: true,
        component: TransporterIdInput,
        onChangeHandler: ({ entity, field, value }) =>
            entity.setIn(field.absoluteAccessor, value),
        extraProps: ({ entity, field }) => {
            return {
                popOverContent: field.validationMessage,
                isPopOverOpen: !!field.validationMessage,
                transporter: entity.getIn([
                    ...field.absoluteAccessor,
                    'transporterId'
                ]),
                getTransporterFilters: input =>
                    transporterIdInputFilters({ input })
            };
        }
    },
    {
        accessor: 'skills',
        type: FieldTypeEnum.Skills,
        options: ({ field }) => {
            const skills = field.fetchInfo.response?.skills || [];
            return skills && skills.map(skill => Skill.fromJS(skill));
        },
        extraProps: ({ field, t }) => {
            return {
                translationsObject: {
                    label: t('vehicleModelForm.skills'),
                    placeholder: t('vehicleModelForm.skillSelectPlaceholder'),
                    addSkillsText: t('vehicleModelForm.addSkill')
                },
                isLoading: !field.fetchInfo.response
            };
        },
        fetchInfo: {
            fetcher: skillsApi.fetch.bind(skillsApi),
            params: ({ props }) => {
                return { clientId: props.clientId, team: [] };
            },
            response: null
        }
    },
    {
        accessor: 'volume.unit',
        type: FieldTypeEnum.Select,
        label: 'vehicleModelForm.volumeUnit',
        helpText: 'vehicleModelForm.volumeUnitTooltip',
        required: true,
        options: volumeUnitOptions,
        dependentAccessors: ['limits', 'volume', 'compartments'],
        onChangeHandler: ({ entity, field, value }) => {
            let updatedEntity = entity.setIn(
                field.absoluteAccessor,
                value?.value
            );
            if (entity.limits?.volume) {
                updatedEntity = updatedEntity.setIn(
                    ['limits', 'volume', 'unit'],
                    value?.value
                );
            }
            return updatedEntity.updateCompartmentsVolumeUnit(value?.value);
        }
    },
    {
        accessor: 'volume',
        component: VehicleCapacity,
        label: 'vehicleModelForm.vehicleCapacity',
        dependentAccessors: ['dimensions', 'volume.unit'],
        required: true,
        extraProps: ({ entity, field, t, props }) => {
            return {
                t,
                entity,
                field,
                unitConverter: props.unitConverter
            };
        },
        onChangeHandler: ({ value: entity }) => entity
    },
    {
        accessor: 'compartments',
        component: VehicleCompartmentsInput,
        label: 'vehicleModelForm.compartments',
        extraProps: ({ t, props }) => {
            return { unitConverter: props.unitConverter, t };
        },
        onChangeHandler: ({ value: entity }) => entity
    },
    {
        accessor: 'allowMultiTrip',
        label: 'vehicleModelForm.allowMultiTrip',
        type: FieldTypeEnum.Boolean,
        dependentAccessors: ['limitMultiTrip'],
        onChangeHandler: ({
            entity,
            value
        }: {
            entity: MasterVehicleModel
        }) => {
            if (!value) {
                return entity.setLimitMultiTrip(false).setAllowMultiTrip(false);
            }
            return entity.setAllowMultiTrip(value);
        },
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('vehicleModelForm.boolean.yes')
                    : t('vehicleModelForm.boolean.no')
            };
        }
    },
    {
        accessor: 'limitMultiTrip',
        label: 'vehicleModelForm.limitMultiTrip',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('vehicleModelForm.boolean.yes')
                    : t('vehicleModelForm.boolean.no')
            };
        }
    },
    {
        accessor: 'maxAdditionalTrips',
        label: 'vehicleModelForm.maxAdditionalTrips',
        type: FieldTypeEnum.Text,
        extraProps: ({ t, entity, field }) => {
            return {
                value: entity.getIn(field.absoluteAccessor),
                pattern: '\\d*',
                placeholder: t('vehicleModelForm.maxAdditionalTripsPlaceholder')
            };
        }
    },
    {
        accessor: 'fleetType',
        type: FieldTypeEnum.Select,
        label: 'vehicleModelForm.fleetType',
        placeholder: 'vehicleModelForm.fleetTypePlaceholder',
        required: true,
        options: FleetTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        label: 'vehicleModelForm.resourceLimits',
        accessor: 'limits',
        component: RiderPersonaLimits,
        dependentAccessors: ['volume'],
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        },
        extraProps: ({ t, entity, props }) => {
            const selectedUnit = {
                volume: entity.volume.unit
            };
            const customFields = CustomColumnHelpers.getCustomFields(
                props.entitySettings,
                'vehicleModelSettings'
            );
            return {
                t,
                limits: entity.limits?.convertToPreferredUnit?.(
                    props.unitConverter,
                    selectedUnit
                ),
                unitConverter: props.unitConverter,
                selectedUnit,
                customFields
            };
        }
    },
    {
        accessor: 'unloadingSide',
        type: FieldTypeEnum.Select,
        label: 'vehicleModelForm.loadingUnloadingSide',
        placeholder: 'vehicleModelForm.loadingUnloadingSidePlaceholder',
        required: true,
        options: LoadingUnloadingSideOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'doorWidth',
        label: 'vehicleModelForm.doorWidth',
        halfWidth: true,
        component: EnhancedInput,
        type: FieldTypeEnum.Text,
        extraProps: ({ t, entity, field }) => {
            return {
                value: entity.getIn(field.absoluteAccessor),
                pattern: '\\d*',
                rightIcon: UnitEnum.Volume.Meters,
                placeholder: t('vehicleModelForm.doorWidthPlaceholder')
            };
        }
    },
    {
        accessor: 'doorHeight',
        label: 'vehicleModelForm.doorHeight',
        halfWidth: true,
        component: EnhancedInput,
        type: FieldTypeEnum.Text,
        extraProps: ({ t, entity, field }) => {
            return {
                value: entity.getIn(field.absoluteAccessor),
                pattern: '\\d*',
                rightIcon: UnitEnum.Volume.Meters,
                placeholder: t('vehicleModelForm.doorHeightPlaceholder')
            };
        }
    },
    {
        accessor: 'loadingParams.loadingDuration',
        label: 'vehicleModelForm.loadingParams.loadingDuration',
        placeholder:
            'vehicleModelForm.loadingParams.loadingDurationPlaceholder',
        type: FieldTypeEnum.TransactionDuration,
        halfWidth: true,
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value * 60)
    },
    {
        accessor: 'loadingParams.unloadingDuration',
        label: 'vehicleModelForm.loadingParams.unloadingDuration',
        placeholder:
            'vehicleModelForm.loadingParams.unloadingDurationPlaceholder',
        type: FieldTypeEnum.TransactionDuration,
        halfWidth: true,
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value * 60)
    },
    {
        accessor: 'isColdChain',
        label: 'vehicleModelForm.isColdChain',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('vehicleModelForm.boolean.yes')
                    : t('vehicleModelForm.boolean.no')
            };
        }
    },
    {
        accessor: 'isDeepFreeze',
        label: 'vehicleModelForm.isDeepFreeze',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('vehicleModelForm.boolean.yes')
                    : t('vehicleModelForm.boolean.no')
            };
        }
    },
    {
        accessor: 'dimensions',
        component: VehicleDimension,
        label: 'vehicleModelForm.dimensions',
        dependentAccessors: ['volume'],
        extraProps: ({ entity, t, props }) => {
            return {
                t,
                entity,
                unitConverter: props.unitConverter
            };
        },
        onChangeHandler: ({ value: entity }) => entity
    },
    {
        accessor: 'maxPayloadWeight',
        label: 'vehicleModelForm.maxPayloadWeight',
        halfWidth: true,
        component: EnhancedInput,
        extraProps: ({ t, entity, field }) => {
            return {
                value: entity.getIn(field.absoluteAccessor),
                error: entity.validateMaxPayloadWeight(),
                pattern: '\\d*',
                rightIcon: UnitEnum.Volume.KiloGram,
                placeholder: t('vehicleModelForm.maxPayloadWeight')
            };
        }
    },
    {
        accessor: 'unladenWeight',
        label: 'vehicleModelForm.unladenWeight',
        halfWidth: true,
        component: EnhancedInput,
        type: FieldTypeEnum.Text,
        extraProps: ({ t, entity, field }) => {
            return {
                value: entity.getIn(field.absoluteAccessor),
                pattern: '\\d*',
                rightIcon: UnitEnum.Volume.KiloGram,
                placeholder: t('vehicleModelForm.unladenWeightPlaceholder')
            };
        }
    },
    {
        accessor: 'travelParams.maxSpeed',
        label: 'vehicleModelForm.travelParams.maxSpeed',
        halfWidth: true,
        type: FieldTypeEnum.Text,
        extraProps: ({ t, entity, field }) => {
            return {
                value: entity.getIn(field.absoluteAccessor),
                pattern: '\\d*',
                rightIcon: UnitEnum.Speed,
                placeholder: t('vehicleModelForm.travelParams.maxSpeed'),
                popOverContent: field.validationMessage,
                isPopOverOpen: !!field.validationMessage
            };
        }
    },
    {
        accessor: 'fuelType',
        type: FieldTypeEnum.Select,
        label: 'vehicleModelForm.fuelType',
        placeholder: 'vehicleModelForm.fuelTypePlaceholder',
        required: true,
        options: FuelTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'userCount',
        label: 'vehicleModelForm.userCount',
        placeholder: 'vehicleModelForm.userCountPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'vehicleModelSettings',
                skipResources: true
            };
        }
    }
];

const VEHICLEMODEL_INITIAL_FETCH_FIELDS = ['skills'];

const SingleVehicleModelEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterVehicleModel,
    MasterActions: VehicleModelActions,
    EntityFetchAPI: vehicleModelApi,
    INITIAL_FETCH_FIELDS: VEHICLEMODEL_INITIAL_FETCH_FIELDS,
    FIELDS: VEHICLEMODEL_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'vehiclemodelId',
        ENTITY_SETTINGS_NAME: 'vehicleModelSettings'
    },
    mapEntityStateToProps: ({ state, props }) => {
        const dimensionsMapping = getClientDimensionsMapping(state, props);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }

        return {
            unitConverter
        };
    }
});

export default SingleVehicleModelEditContainer;
